import type { ComponentType, ReactNode } from 'react';

import type { CaptionProps } from '../Caption/Caption';

import { SyncComboBoxBase } from './SyncComboBoxBase';
import type { ComboBoxChangeMeta } from './types/ComboBoxChangeMeta';
import type { ComboBoxOptionComponentProps, ComboBoxValueComponentProps } from './types/ComboBoxComponentProps';
import { ComboBoxMode } from './types/ComboBoxMode';
import type { ComboBoxOption } from './types/ComboBoxOption';
import type { ComboBoxValue } from './types/ComboBoxValue';

export type { ComboBoxChangeMeta } from './types/ComboBoxChangeMeta';
export type { ComboBoxOption } from './types/ComboBoxOption';
export type { ComboBoxValue, MultiComboBoxValue } from './types/ComboBoxValue';
export { ComboBoxDoubleLineOption } from './components/ComboBoxDoubleLineOption';
export { AsyncComboBox } from './AsyncComboBox';
export type { AsyncComboBoxProps } from './AsyncComboBox';
export { AsyncMultiComboBox } from './AsyncMultiComboBox';
export type { AsyncMultiComboBoxProps } from './AsyncMultiComboBox';
export { MultiComboBox } from './MultiComboBox';
export type { MultiComboBoxProps } from './MultiComboBox';
export { PaginatedAsyncComboBox } from './PaginatedAsyncComboBox';
export type { PaginatedAsyncComboBoxProps } from './PaginatedAsyncComboBox';
export { PaginatedAsyncMultiComboBox } from './PaginatedAsyncMultiComboBox';

interface ComboBoxSharedProps<Option extends ComboBoxOption> {
    options?: Option[];
    label?: string;
    labelInfo?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    caption?: ReactNode;
    CaptionIcon?: CaptionProps['Icon'];
    disabled?: boolean;
    positive?: boolean;
    negative?: boolean;
    markAsRequired?: boolean;
    markAsOptional?: boolean;
    description?: string;
    isClearable?: boolean;
    isSearchable?: boolean;
    minWidth?: string;
    dropdownWidth?: string;
    dropdownMaxHeight?: string;
    noOptionsMessage?: string;
    onBlur?: () => void;
    LeadingIcon?: ComponentType<{ width?: number; height?: number }>;
    OptionSeparatorComponent?: ComponentType<ComboBoxOptionComponentProps<Option>>;
}

interface ComboBoxSingleSelectionProps<Option extends ComboBoxOption> {
    value?: ComboBoxValue<Option>;
    onChange?: (value: ComboBoxValue<Option>, meta: ComboBoxChangeMeta<Option>) => void;
    OptionComponent?: ComponentType<ComboBoxOptionComponentProps<Option>>;
    ValueComponent?: ComponentType<ComboBoxValueComponentProps<Option>>;
}

export type ComboBoxProps<Option extends ComboBoxOption = ComboBoxOption> = ComboBoxSharedProps<Option> &
    ComboBoxSingleSelectionProps<Option>;

export function ComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    value,
    onChange,
    isClearable = false,
    ...props
}: ComboBoxProps<Option>) {
    return (
        <SyncComboBoxBase<Option>
            {...props}
            mode={ComboBoxMode.Single}
            value={value}
            isClearable={isClearable}
            onSelectionChange={(nextSelectedOptions, meta) => {
                onChange?.(nextSelectedOptions[0]?.value ?? null, meta);
            }}
        />
    );
}
